<template>
  <div class="card">
    <div class="attendance-layout" v-if="tumorBoardAttendanceData.length > 0">
      <div class="attendance-table-header-div">
        <span class="material-symbols-outlined attendance-icon">group</span>
        <div class="number-of-records-div" v-if="tumorBoardAttendanceData.length">
          <span class="tab-count">{{ tumorBoardAttendanceData.length }}</span>
        </div>
        <div class="attendance-heading-div">
          <h3 class="attendance-heading">Attendees</h3>
        </div>
        <div class="search-physician-div">
          <span class="p-input-icon-right">
            <i class="material-symbols-outlined small-x" v-show="searchQuery" @click="clearSearch">close</i>
            <i class="material-symbols-outlined small-x" v-show="!searchQuery">search</i>
            <InputText v-model="searchQuery" type="text" size="small" placeholder="Search Physician" />
          </span>
        </div>
        <div v-if="isUserTBPersonnel && isTodaysDate" class="mail-icon-div">
          <button @click="sendMailFunction()" class="primary-filled-button cme-credits-button">Request CME
            Credits</button>
        </div>
        <div v-if="isUserTBPersonnel && isTodaysDate" class="save-button-div">
          <button @click="handleSaveAttendanceData()"
            class="material-symbols-outlined icon-button-to-primary save-button">save</button>
        </div>
      </div>
      <div class="attendance-table-div">
        <DataTable :value="filteredTumorBoardAttendanceData" scrollable scrollHeight="50vh"
          tableStyle="min-width: 5rem">
          <Column header="" style="width: 5%">
            <template #body="slotProps">
              <span>{{ slotProps.index + 1 }}</span>
            </template>
          </Column>
          <Column field="name" header="PHYSICIAN NAME" sortable editable style="width: 15%"></Column>
          <Column field="department_name" header="SPECIALTY" sortable style="width: 15%"></Column>
          <Column v-if="isUserTBPersonnel && isTodaysDate" header="ATTENDANCE" style="width: 65%">
            <template #body="rowData">
              <div v-if="rowData">
                <label class="checkbox-label">{{ rowData.data.is_present ? "Present" : "Absent" }}</label>
                <Checkbox v-model="rowData.data.is_present" :binary="true" />
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 Omics Data Automation, Inc.
// Copyright (c) 2023 datma, inc.
//
// Proprietary and Confidential:
// Unauthorized copying of this file by any medium is strictly prohibited.

import { computed, ref, watch } from 'vue'
import { getAttendanceListData } from './getAttendanceListData.js'
import { useStore } from 'vuex'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Checkbox from 'primevue/checkbox'
import InputText from 'primevue/inputtext'
import { patchAttendanceData } from './patchAttendanceData'
import { sendAttendanceMail } from './sendAttendanceMail'
import { formatDateToTBPayload, showToast } from '@/utils/utils.js'
import { getExternalUsers } from '@/ux3/apis/externalUser.js'

const store = useStore()
const meeting = computed(() => store.state.session.meeting)
const tumorBoardAttendanceData = ref([])
const userRoles = computed(() => store.getters.userRoles)
const isUserTBPersonnel = ref(false)
const isTodaysDate = ref(false)
const searchQuery = ref('')
const filteredTumorBoardAttendanceData = computed(() => {
  const query = searchQuery.value.toLowerCase().trim()
  const nameInclQuery = (name) => name.toLowerCase().includes(query)
  const itemInclQuery = (item) => nameInclQuery(item.name) || nameInclQuery(item.department_name)
  return tumorBoardAttendanceData.value.filter((item) => itemInclQuery(item))
})

const clearSearch = () => searchQuery.value = ''

watch(meeting, async (newMeeting) => {
  if (!meeting.value || !newMeeting) { return }
  const tbId = meeting.value.data.calendar.tb_id
  const date = meeting.value.data.calendar.date
  const tumorBoardAttendanceDataFromResponse = await getAttendanceListData(tbId, date)
  const allExternalUsers = await getExternalUsers()
  const externalUsers = allExternalUsers.result.filter(user => user.tumorboard_ids.find(id => id === tbId))

  isUserTBPersonnel.value = userRoles.value.includes('tb-personnel')
  isTodaysDate.value = date === formatDateToTBPayload(new Date())

  const sortedData = tumorBoardAttendanceDataFromResponse.sort((a, b) => {
    return a.department_name.localeCompare(b.department_name)
  })
  const allUsers = [...sortedData, ...externalUsers]
  tumorBoardAttendanceData.value = allUsers

  store.dispatch('setTumorBoardAttendanceData', {
    tumorBoardAttendanceData: tumorBoardAttendanceData.value,
    publish: true,
  })
}, { immediate: true })

const handleSaveAttendanceData = async () => {
  const attendeesCaregiverIds = tumorBoardAttendanceData.value?.filter((attendee) => attendee.is_present)
    .map((attendee) => attendee.care_giver_id)

  const attendanceSavedData = {
    tb_id: meeting.value.data.tb_id,
    date: meeting.value.data.date,
    attendees: attendeesCaregiverIds,
  }
  await patchAttendanceData(attendanceSavedData)
  showToast('success', 'Changes Saved')
}

const sendMailFunction = async () => {
  const tumorBoardID = meeting.value.data.tb_id
  const date = meeting.value.data.date
  const mailResponse = await sendAttendanceMail(date, tumorBoardID)
  mailResponse.detail ? showToast('error', 'Error sending mail') : showToast('success', 'Mail sent successfully')
  if (mailResponse.detail) { console.log(mailResponse.detail[0].msg) }
}
</script>

<style>
.p-sortable-column .p-sortable-column-icon {
  /* Change the color of the sort icon here */
  color: var(--Grey600) !important;
  /* TODO: eliminate !important */
}

.number-of-records-span {
  color: var(--Grey900);
  background-color: var(--Grey300);
  padding: 4px 7px;
  border-radius: 24px;
  font-size: 0.7rem;
  font-weight: bolder;
  margin-top: -18px;
}

.number-of-records-div {
  display: flex;
  align-items: center;
}

.attendance-heading {
  margin-left: 16px;
  font-size: 14px;
  font-weight: 600;
}

.attendance-heading-div {
  flex-grow: 1;
  text-align: initial;
}

.user-icon-div {
  display: flex;
  justify-content: center;
}

.attendance-table-header-div {
  display: flex;
  align-items: center;
  background-color: var(--White);
  justify-content: space-between;
  border-top-right-radius: 15px;
  border-top-left-radius: 15px;
  margin: 0.5% 28px 0 28px;
}

.attendance-layout {
  border: 1px solid var(--Grey200);
  margin: 28px;
  border-radius: 6px;
  background-color: var(--White);
  margin-bottom: 5%;
}

.save-button-div {
  display: flex;
  align-items: center;
}

.save-button {
  color: var(--Primary)
}

.small-x {
  font-size: 16px;
  cursor: pointer
}

.checkbox-label {
  margin-right: 7%;
}

.mail-icon-div {
  display: flex;
  align-items: center;
}

.mail-icon-span {
  padding: 3px;
  border-radius: 8px;
  cursor: pointer;
}

.search-physician-div {
  display: flex;
  align-items: center;
  margin-right: 8px;
}

.p-input-icon-right>i:first-of-type {
  right: 0.75rem;
  color: var(--Grey700);
}
</style>

<style>
.attendance-table-div .p-column-header-content {
  font-size: 12px;
  font-weight: 500;
  color: var(--Grey600);
  font-family: 'Roboto';
}

.attendance-table-div .p-datatable .p-datatable-tbody>tr>td {
  font-size: 14px;
  font-weight: 400;
  color: var(--Black);
  font-family: 'Roboto';
}

.attendance-table-div .p-datatable .p-sortable-column:first-child .p-sortable-column-icon {
  margin-left: 0;
}

.search-physician-div .p-inputtext {
  height: 33px;
  width: 160px;
  background-color: var(--Grey200);
}

.attendance-icon {
  margin-left: 1rem;
}

.cme-credits-button {
  color: var(--Grey800);
  background-color: var(--Grey200);
  border: 1px solid var(--Grey200);
}

.cme-credits-button:hover,
.cme-credits-button:focus,
.cme-credits-button:active {
  border: 1px solid var(--Grey400);
  background-color: var(--Grey200);
  color: var(--Grey800);
}
</style>
